// UsageAnalytics.tsx
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { AlertTriangle, Loader2, Shield, Activity, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API_BASE } from '@/lib/api';

interface UserMetrics {
  userId: string;
  email: string;
  name: string;
  metrics: {
    totalTokens: number;
    sessionsCount: number;
    avgTokensPerSession: number;
    riskIndicators: {
      rapidFireSessions: number;
      highTokenSessions: number;
      offHourActivity: number;
    };
    riskScore: number;
    lastActive: string;
  };
}

interface AnalyticsResponse {
  users: UserMetrics[];
  _ts: number;
}

const fetchAnalytics = async (token: string): Promise<AnalyticsResponse> => {
  const response = await fetch(`${API_BASE}/admin-usage-analytics`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch analytics');
  }

  return response.json();
};

const getRiskLevel = (score: number) => {
  if (score >= 70) return { label: 'High Risk', color: 'bg-red-500/10 text-red-400 border-red-500/20' };
  if (score >= 40) return { label: 'Medium Risk', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
  return { label: 'Low Risk', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' };
};

const formatNumber = (num: number) => new Intl.NumberFormat().format(num);

export default function UsageAnalytics() {
  const { getToken } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-usage-analytics'],
    queryFn: async () => {
      const token = await getToken();
      return fetchAnalytics(token!);
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-white" />
          <p className="mt-3 text-sm text-slate-300">Loading usage analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-slate-700 bg-slate-900">
        <CardHeader className="space-y-2 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-red-400" />
          <CardTitle className="text-white">Unable to load analytics</CardTitle>
          <CardDescription className="text-slate-300">
            {error instanceof Error ? error.message : 'Please try again later.'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!data?.users) {
    return null;
  }

  return (
    <Card className="border-slate-700 bg-slate-900">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-white">Usage Analytics</CardTitle>
            <CardDescription className="text-slate-400">
              Token usage patterns and risk analysis
            </CardDescription>
          </div>
          <Shield className="h-5 w-5 text-emerald-400" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          <div className="rounded-lg border border-slate-700 overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-12 gap-2 p-4 text-xs uppercase tracking-wider text-slate-300 bg-slate-950/50 border-b border-slate-700">
                <div className="col-span-3 !text-white">User</div>
                <div className="col-span-2 !text-white">Total Tokens</div>
                <div className="col-span-1 !text-white">Sessions</div>
                <div className="col-span-2 !text-white">Avg Tokens/Session</div>
                <div className="col-span-2 !text-white">Risk Level</div>
                <div className="col-span-2 !text-white">Actions</div>
              </div>

              <div className="divide-y divide-slate-700">
                {data.users.map((user) => {
                  const risk = getRiskLevel(user.metrics.riskScore);
                  return (
                    <div
                      key={user.userId}
                      className="grid grid-cols-12 gap-2 p-4 text-sm hover:bg-slate-800/30 transition-colors"
                    >
                      <div className="col-span-3">
                        <p className="font-medium text-white truncate">{user.name || 'Anonymous'}</p>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                      </div>
                      <div className="col-span-2 flex items-center text-white">
                        {formatNumber(user.metrics.totalTokens)}
                      </div>
                      <div className="col-span-1 flex items-center text-white">
                        {formatNumber(user.metrics.sessionsCount)}
                      </div>
                      <div className="col-span-2 flex items-center text-white">
                        {formatNumber(user.metrics.avgTokensPerSession)}
                      </div>
                      <div className="col-span-2 flex items-center">
                        <Badge className={risk.color}>
                          {risk.label}
                        </Badge>
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 !text-slate-400 hover:text-white hover:bg-slate-800"
                          title="View details"
                        >
                          <Activity className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 !text-red-400 hover:text-red-300 hover:bg-red-950/30"
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-amber-500/5 p-4 text-sm border border-amber-500/10">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-amber-300">Risk Score Components:</p>
                <ul className="list-disc pl-4 space-y-1 text-xs text-amber-200/80">
                  <li className='!text-amber-200/80'>Rapid-fire sessions (multiple sessions within 60s)</li>
                  <li className='!text-amber-200/80'>High token usage (2x average per session)</li>
                  <li className='!text-amber-200/80'>Off-hour activity (2 AM - 5 AM)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
